import { useEffect, useState } from 'react'

const SCRIPT_SRC = 'https://chat-assets.frontapp.com/v1/chat.bundle.js'

type FrontChatParams = {
	chatId?: string
	email?: string
	userHash?: string
	onInitCompleted?: () => void
	[key: string]: unknown
}

type FrontChat = (cmdType: string, params?: FrontChatParams) => void

declare global {
	interface Window {
		FrontChat?: FrontChat
	}
}

const loadFrontChat = (): Promise<FrontChat> => {
	if (typeof window === 'undefined') {
		return Promise.reject(
			new Error('Front Chat is only available in the browser'),
		)
	}

	if (window.FrontChat) {
		return Promise.resolve(window.FrontChat)
	}

	return new Promise((resolve, reject) => {
		const existingScript = document.querySelector<HTMLScriptElement>(
			`script[src="${SCRIPT_SRC}"]`,
		)

		if (existingScript) {
			existingScript.addEventListener('load', () => {
				if (window.FrontChat) {
					resolve(window.FrontChat)
					return
				}

				reject(new Error('[front-chat] Could not set up window.FrontChat'))
			})
			return
		}

		const script = document.createElement('script')
		script.src = SCRIPT_SRC
		script.type = 'text/javascript'
		script.onload = () => {
			if (!window.FrontChat) {
				reject(new Error('[front-chat] Could not set up window.FrontChat'))
				return
			}

			resolve(window.FrontChat)
		}
		document.body.appendChild(script)
	})
}

export const useFrontChat = (chatId: string) => {
	const [frontChat, setFrontChat] = useState<FrontChat>()

	useEffect(() => {
		if (!chatId) return

		let cancelled = false

		loadFrontChat()
			.then((api) => {
				if (cancelled) return

				api('init', { chatId })
				setFrontChat(() => api)
			})
			.catch(console.error)

		return () => {
			cancelled = true
		}
	}, [chatId])

	return { frontChat }
}
